/* eslint-disable import/no-cycle */
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, Logger } from '@saviynt/common';
import {
  ButtonIcon,
  Icon,
  Loader,
  Menu,
  MobileDrawer,
  MobileDrawerItem,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import useResponsive from '../../../hooks/use-responsive';
import {
  performCertsListExport,
  sendNotifyEmail,
  setTempActionState,
} from '../../../redux/certlist/action';
import { getMessageKey, handleRedirectOnClick } from '../../../utils/utils';
import {
  CAMP_TYPE_AO,
  CAMP_TYPE_EO,
  CAMP_TYPE_SA,
  CAMP_TYPE_SC,
  CAMP_TYPE_UM,
} from '../CertsListTile/constant';
import {
  ACT_DISCONTINUE,
  ACT_NOTIFY,
  ACTION_BUTTON_FOOTER_KIND,
} from '../constant';

import CertsListActionComponent from './CertsListActionComponent';
import { ACT_EXPORT, ACT_REASSIGN } from './constant';

function CertsListActionMenu({
  options,
  certKey,
  isTabletWidth,
  kind,
  tileCampaignKey,
  campaignType,
  newCertUX,
  certDetailsUrl,
  footerText,
  isReassignAPrimaryAction,
  handleTileClick,
  certificationName,
  tileData,
}) {
  const dispatch = useDispatch();
  const [selectedAction, setSelectedAction] = useState(null);
  const [openAction, setOpenAction] = useState(null);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const { isMobileXlargeWidth } = useResponsive();
  const [enableOutsideClick, setEnableOutsideClick] = useState(true);

  const { lineAction: { certsActionIsLoading } = {} } = useSelector(
    (state) => state?.certifications?.list
  );

  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user) || {};

  const handleNotifyEmail = async () => {
    try {
      const result = await dispatch(sendNotifyEmail(certKey));

      if (result.success) {
        setSelectedAction(null);
      } else {
        Logger.error('Notify Email API call failed');
      }
    } catch (error) {
      Logger.error('Notify Email API call error:', error);
    }
  };

  const handleChange = (selectedOption) => {
    if (selectedOption.value === 'continue') {
      handleRedirectOnClick(newCertUX, certDetailsUrl);
    } else if (selectedOption.value === 'see-details') {
      handleTileClick();
      setIsMobileDrawerOpen(false);
    } else if (selectedOption.value === 'notify') {
      handleNotifyEmail();
    } else if (
      selectedOption.value === 'export' &&
      !(
        (campaignType === CAMP_TYPE_UM ||
          campaignType === CAMP_TYPE_AO ||
          campaignType === CAMP_TYPE_SA ||
          campaignType === CAMP_TYPE_EO ||
          campaignType === CAMP_TYPE_SC) &&
        newCertUX === '1'
      )
    ) {
      // Show Export Modal only in case of campaingType is User Manger
      // Default export in case of all other campaingType

      dispatch(performCertsListExport(certKey, false));
    } else {
      setSelectedAction(selectedOption.value);
    }
  };

  useEffect(() => {
    setOpenAction(selectedAction);

    setEnableOutsideClick(selectedAction === null);
  }, [selectedAction]);

  const handleSetIsOpen = (action, isOpen) => {
    if (!isOpen) {
      setOpenAction(null);
      setSelectedAction(null);
    } else {
      setOpenAction(action);
    }
  };

  const optionsForMobile = options
    .filter((option) => option.value !== ACT_EXPORT)
    .map((option) => ({
      label: option.label,
      value: option.value,
      isChecked: false,
      isDisabled: false,
      prefixIcon: option.icon,
    }));

  if (kind === ACTION_BUTTON_FOOTER_KIND.tile) {
    optionsForMobile.unshift({
      label: footerText,
      value: 'continue',
      isChecked: false,
      isDisabled: false,
      prefixIcon: 'ArrowRight',
    });

    optionsForMobile.push({
      label:
        messages.CERTS_ACTION_MENU_SEE_DETAILS ||
        localize(intl, getMessageKey('CERTS_ACTION_MENU_SEE_DETAILS')),
      value: 'see-details',
      suffixIcon: 'ChevronRightSmall',
      isChecked: false,
      isDisabled: false,
    });
  }

  const optionsForDesktop =
    isReassignAPrimaryAction && kind !== ACTION_BUTTON_FOOTER_KIND.footer
      ? [
          ...options.filter((action) => action?.value === ACT_DISCONTINUE),
          {
            label: footerText,
            value: 'continue',
            isChecked: false,
            isDisabled: false,
            icon: 'ArrowRight',
          },
          ...options.filter(
            (action) =>
              action?.value !== ACT_REASSIGN &&
              action?.value !== ACT_DISCONTINUE
          ),
        ]
      : options;

  const mobileDrawerHeading =
    kind === ACTION_BUTTON_FOOTER_KIND.footer ? 'More' : 'All Actions';

  return (
    <>
      {isMobileXlargeWidth ? (
        <>
          {optionsForMobile?.length ? (
            <ButtonIcon
              size='small'
              kind='ghost'
              onClick={() => {
                setIsMobileDrawerOpen(true);
                dispatch(setTempActionState(true));
              }}
              icon={
                <Icon
                  kind='MoreVertical'
                  color='neutral-000'
                  size='smallMedium'
                />
              }
            />
          ) : null}

          <MobileDrawer
            className='CertListActionMenu-MobileDrawer'
            isOpen={isMobileDrawerOpen}
            setIsOpen={() => {
              setIsMobileDrawerOpen(!isMobileDrawerOpen);
              dispatch(setTempActionState(!isMobileDrawerOpen));
            }}
            isCloseButtonEnabled={false}
            headerContent={
              <Typography kind='h4' desktop='h4-d' mobile='h4-d'>
                {mobileDrawerHeading}
              </Typography>
            }
            isOutsideClickEnabled={enableOutsideClick}
            content={
              <>
                {optionsForMobile.map((option) => (
                  <MobileDrawerItem
                    key={option.value}
                    option={option}
                    onClick={() => handleChange(option)}
                    prefixIcon={
                      option.prefixIcon && (
                        <Icon
                          kind={option.prefixIcon}
                          color='--color-foreground-secondary-bold'
                        />
                      )
                    }
                    suffixIcon={
                      option.value === ACT_NOTIFY && certsActionIsLoading ? (
                        <Loader kind='dots' format='inline' color='brand' />
                      ) : null
                    }
                  />
                ))}
              </>
            }
          />
        </>
      ) : (
        <Menu
          options={optionsForDesktop}
          onChange={handleChange}
          trigger={
            <ButtonIcon
              size={isTabletWidth ? 'small' : 'medium'}
              kind='ghost'
              icon={
                <Icon kind='MoreVertical' color='neutral-100' size='medium' />
              }
            />
          }
        />
      )}

      <CertsListActionComponent
        action={openAction}
        isOpen={Boolean(openAction)}
        setIsOpen={handleSetIsOpen}
        certKey={certKey}
        tileCampaignKey={tileCampaignKey}
        certificationName={certificationName}
        campaignType={campaignType}
        tileData={tileData}
      />
    </>
  );
}

CertsListActionMenu.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      icon: PropTypes.string,
    })
  ).isRequired,
  certKey: PropTypes.number.isRequired,
  isTabletWidth: PropTypes.bool.isRequired,
  kind: PropTypes.string.isRequired,
  tileCampaignKey: PropTypes.number.isRequired,
  campaignType: PropTypes.string.isRequired,
  newCertUX: PropTypes.string.isRequired,
  certDetailsUrl: PropTypes.string.isRequired,
  footerText: PropTypes.string.isRequired,
  isReassignAPrimaryAction: PropTypes.bool,
  handleTileClick: PropTypes.func,
  certificationName: PropTypes.string.isRequired,
  tileData: PropTypes.shape({}),
};

CertsListActionMenu.defaultProps = {
  isReassignAPrimaryAction: false,
  handleTileClick: () => {},
  tileData: null,
};

export default CertsListActionMenu;
